import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Button from '../../components/ui/Button';
import StatusIndicator from '../../components/ui/StatusIndicator';
import PublicNavigation from '../../components/ui/PublicNavigation';
import DeliveryProofSection from './components/DeliveryProofSection';
import { clientAuthService } from '../../services/clientAuthService';
import { supabase } from '../../lib/supabase';

export default function ClientShipmentDetail() {
  const { shipmentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [shipment, setShipment] = useState(location?.state?.shipment || null);
  const [events, setEvents] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [deliveryTask, setDeliveryTask] = useState(null);
  const [loading, setLoading] = useState(!shipment);
  const [error, setError] = useState('');
  // Pickup proof state
  const [pickupTask, setPickupTask] = useState(null);
  const [pickupPhotoUrls, setPickupPhotoUrls] = useState([]);
  const [loadingPickupPhotos, setLoadingPickupPhotos] = useState(false);

  useEffect(() => {
    loadShipmentDetail();
  }, [shipmentId]);

  const loadShipmentDetail = async () => {
    try {
      setLoading(true);

      // Check authentication
      if (!clientAuthService?.isAuthenticated()) {
        navigate('/login');
        return;
      }

      // If shipment not passed via state, fetch it via RPC (same as dashboard)
      if (!shipment) {
        const clientData = JSON.parse(localStorage.getItem('client_data'));
        if (!clientData?.id || !clientData?.phone) {
          navigate('/login');
          return;
        }

        const { data: rpcData, error: rpcError } = await supabase?.rpc('get_client_dashboard_data', {
          p_client_id: clientData?.id,
          p_phone: clientData?.phone
        });

        if (rpcError) throw rpcError;

        const rpcShipments = Array.isArray(rpcData?.shipments) ? rpcData?.shipments : [];
        const found = rpcShipments?.find(s => s?.id === shipmentId);

        if (!found) {
          setLoading(false);
          return;
        }
        setShipment(found);
      }

      // Load shipment events
      const { data: eventsData, error: eventsError } = await supabase?.from('shipment_events')?.select('*')?.eq('shipment_id', shipmentId)?.order('event_date', { ascending: false });

      if (eventsError) throw eventsError;
      setEvents(eventsData || []);

      // Load shipment documents
      const { data: docsData, error: docsError } = await supabase?.from('shipment_files')?.select('*')?.eq('shipment_id', shipmentId)?.order('created_at', { ascending: false });

      if (docsError) throw docsError;
      setDocuments(docsData || []);

      // Load delivery task (get the most recent one for this shipment)
      const { data: deliveryData, error: deliveryError } = await supabase
        ?.from('delivery_tasks')
        ?.select('*')
        ?.eq('shipment_id', shipmentId)
        ?.order('created_at', { ascending: false })
        ?.limit(1)
        ?.single();

      // Don't throw error if no delivery task exists yet
      if (deliveryError && deliveryError?.code !== 'PGRST116') {
        console.error('Error loading delivery task:', deliveryError);
      } else if (deliveryData) {
        setDeliveryTask(deliveryData);
      }

      // Load pickup task for this shipment (to show pickup proof)
      try {
        const { data: pickupData, error: pickupError } = await supabase
          ?.from('pickup_tasks')
          ?.select('id, status, client_signature_url, pickup_photo_urls, date, time_window')
          ?.eq('shipment_id', shipmentId)
          ?.eq('status', 'termine')
          ?.order('updated_at', { ascending: false })
          ?.limit(1)
          ?.single();

        if (!pickupError && pickupData) {
          setPickupTask(pickupData);
          // Generate signed URLs for pickup photos
          if (pickupData?.pickup_photo_urls?.length > 0) {
            await loadPickupPhotoSignedUrls(pickupData?.pickup_photo_urls);
          }
        }
      } catch (pickupErr) {
        // No pickup task found - not an error
        console.log('No completed pickup task found for this shipment');
      }
    } catch (err) {
      setError(err?.message || 'Erreur lors du chargement des détails');
    } finally {
      setLoading(false);
    }
  };

  const loadPickupPhotoSignedUrls = async (photoValues) => {
    if (!photoValues || photoValues?.length === 0) return;
    setLoadingPickupPhotos(true);
    try {
      const signedUrls = await Promise.all(
        photoValues?.map(async (photoValue) => {
          if (!photoValue) return null;
          let filePath = '';
          if (photoValue?.startsWith('pickup-photos/')) {
            filePath = photoValue;
          } else if (photoValue?.includes('/storage/v1/object/public/shipment-photos/')) {
            const parts = photoValue?.split('/storage/v1/object/public/shipment-photos/');
            filePath = parts?.[1] || '';
          } else {
            return photoValue;
          }
          if (!filePath) return null;
          const { data, error } = await supabase?.storage?.from('shipment-photos')?.createSignedUrl(filePath, 3600);
          if (error || !data?.signedUrl) return null;
          return data?.signedUrl;
        })
      );
      setPickupPhotoUrls(signedUrls?.filter(Boolean));
    } catch (err) {
      console.error('Error loading pickup photo signed URLs:', err);
    } finally {
      setLoadingPickupPhotos(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800 border-yellow-300',
      'in_transit': 'bg-blue-100 text-blue-800 border-blue-300',
      'customs': 'bg-purple-100 text-purple-800 border-purple-300',
      'out_for_delivery': 'bg-indigo-100 text-indigo-800 border-indigo-300',
      'delivered': 'bg-green-100 text-green-800 border-green-300',
      'exception': 'bg-red-100 text-red-800 border-red-300'
    };
    return colors?.[status] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pending': 'En attente',
      'in_transit': 'En transit',
      'customs': 'En douane',
      'out_for_delivery': 'En cours de livraison',
      'delivered': 'Livré',
      'exception': 'Exception'
    };
    return labels?.[status] || status;
  };

  const handleDownloadDocument = async (doc) => {
    try {
      if (!doc) {
        alert('Document introuvable');
        return;
      }

      // If doc has a direct URL (document_url), open it directly
      const directUrl = doc?.url || doc?.document_url || doc?.file_url;
      if (directUrl) {
        window.open(directUrl, '_blank');
        return;
      }

      let filePath = doc?.file_path || doc?.path;
      if (!filePath) {
        alert('Chemin du fichier introuvable');
        return;
      }

      // Get signed URL for download
      const { data, error } = await supabase?.storage?.from('shipment-documents')?.createSignedUrl(filePath, 60); // 60 seconds expiry

      if (error) throw error;
      
      // Open in new tab
      window.open(data?.signedUrl, '_blank');
    } catch (err) {
      console.error('Download error:', err);
      alert('Erreur lors du téléchargement du document');
    }
  };

  const getPhotoUrls = (shipment) => {
    const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
    const bucket = 'shipment-photos';
    const toPublicUrl = (path) => {
      if (!path) return null;
      if (path?.startsWith('http')) return path;
      return `${supabaseUrl}/storage/v1/object/public/${bucket}/${path}`;
    };

    const urls = [];

    // photos_urls array (priority)
    if (shipment?.photos_urls) {
      const arr = Array.isArray(shipment?.photos_urls)
        ? shipment?.photos_urls
        : typeof shipment?.photos_urls === 'string'
        ? JSON.parse(shipment?.photos_urls)
        : [];
      arr?.forEach((p) => {
        const u = toPublicUrl(p);
        if (u) urls?.push(u);
      });
    }

    // photo_url single field
    if (shipment?.photo_url) {
      const u = toPublicUrl(shipment?.photo_url);
      if (u && !urls?.includes(u)) urls?.push(u);
    }

    return urls;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <svg className="animate-spin h-12 w-12 text-green-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-gray-600">Chargement des détails...</p>
        </div>
      </div>
    );
  }

  if (!shipment) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Envoi non trouvé</h2>
          <Link to="/client-dashboard">
            <Button variant="primary">Retour au tableau de bord</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Détails envoi {shipment?.reference} - ADT Tracking</title>
        <meta name="description" content={`Détails de l'envoi ${shipment?.reference}`} />
      </Helmet>
      <div className="min-h-screen bg-gray-50">
        <PublicNavigation />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* Header Card */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  Envoi {shipment?.reference}
                </h1>
                <p className="text-gray-600 text-sm">
                  Créé le {new Date(shipment.created_at)?.toLocaleDateString('fr-FR', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </p>
              </div>
              <div className={`mt-4 md:mt-0 inline-flex items-center px-4 py-2 rounded-full border ${getStatusColor(shipment?.current_status)}`}>
                <StatusIndicator status={shipment?.current_status} label={getStatusLabel(shipment?.current_status)} />
                <span className="ml-2 font-medium">{getStatusLabel(shipment?.current_status)}</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Shipment Details */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-lg font-bold text-gray-900 mb-4">Informations d'expédition</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Expéditeur</h3>
                    <div className="text-sm text-gray-900">
                      <p className="font-medium">{shipment?.sender_name}</p>
                      <p>{shipment?.sender_address}</p>
                      <p>{shipment?.sender_city}, {shipment?.sender_country}</p>
                      <p className="mt-1 text-gray-600">{shipment?.sender_phone}</p>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Destinataire</h3>
                    <div className="text-sm text-gray-900">
                      <p className="font-medium">{shipment?.receiver_name}</p>
                      <p>{shipment?.receiver_address}</p>
                      <p>{shipment?.receiver_city}, {shipment?.receiver_country}</p>
                      <p className="mt-1 text-gray-600">{shipment?.receiver_phone}</p>
                    </div>
                  </div>
                </div>

                {shipment?.description && (
                  <div className="mt-4 pt-4 border-t">
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Description</h3>
                    <p className="text-sm text-gray-900">{shipment?.description}</p>
                  </div>
                )}
              </div>

              {/* Timeline */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-lg font-bold text-gray-900 mb-4">Historique de suivi</h2>
                {events?.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    Aucun événement pour le moment
                  </div>
                ) : (
                  <div className="space-y-4">
                    {events?.map((event, index) => (
                      <div key={event?.id} className="relative pl-8 pb-6 last:pb-0">
                        {index < events?.length - 1 && (
                          <div className="absolute left-2 top-6 h-full w-0.5 bg-gray-200"></div>
                        )}
                        <div className="absolute left-0 top-1 w-4 h-4 rounded-full bg-green-600 border-2 border-white"></div>
                        <div>
                          <div className="text-sm font-medium text-gray-900 mb-1">
                            {event?.event_type}
                          </div>
                          {event?.location && (
                            <div className="text-xs text-gray-600 mb-1">
                              📍 {event?.location}
                            </div>
                          )}
                          {event?.description && (
                            <div className="text-xs text-gray-600 mb-1">
                              {event?.description}
                            </div>
                          )}
                          <div className="text-xs text-gray-500">
                            {new Date(event.event_date)?.toLocaleString('fr-FR')}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Documents */}
              {(shipment?.document_url || documents?.length > 0) && (
                <div className="bg-white rounded-lg shadow-md p-6">
                  
                  {/* Main Document */}
                  {shipment?.document_url && (
                    <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center flex-1">
                          <svg className="w-10 h-10 text-blue-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <div>
                            <div className="text-sm font-semibold text-blue-900">Document principal de l'envoi</div>
                            <div className="text-xs text-blue-700">Document officiel lié à cet envoi</div>
                          </div>
                        </div>
                        <a
                          href={shipment?.document_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-4 text-blue-600 hover:text-blue-700"
                        >
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                        </a>
                      </div>
                    </div>
                  )}
                  
                  {/* Additional Documents */}
                  {documents?.length > 0 && (
                    <div className="space-y-3">
                      {shipment?.document_url && (
                        <h3 className="text-sm font-semibold text-gray-700 mt-4 mb-2">Documents supplémentaires</h3>
                      )}
                      {documents?.map((doc) => (
                        <div
                          key={doc?.id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center flex-1">
                            <svg className="w-8 h-8 text-gray-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                            <div>
                              <div className="text-sm font-medium text-gray-900">{doc?.file_name}</div>
                              <div className="text-xs text-gray-500">
                                {doc?.file_type} • {new Date(doc.created_at)?.toLocaleDateString('fr-FR')}
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => handleDownloadDocument(doc)}
                            className="ml-4 text-green-600 hover:text-green-700"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* No Documents Message */}
                  {!shipment?.document_url && documents?.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <svg className="w-16 h-16 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p className="text-sm">Aucun document disponible pour cet envoi</p>
                    </div>
                  )}
                </div>
              )}

              {/* Delivery Proof Section */}
              <DeliveryProofSection deliveryTask={deliveryTask} />

              {/* Pickup Proof Section - shown when pickup is completed */}
              {pickupTask && (
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Preuve d'enlèvement
                  </h2>

                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 text-sm text-green-800">
                    ✅ Votre colis a été collecté par notre chauffeur
                    {pickupTask?.date && (
                      <span> le {new Date(pickupTask?.date)?.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                    )}
                    {pickupTask?.time_window && <span> ({pickupTask?.time_window})</span>}
                  </div>

                  {/* Pickup Photos */}
                  {(pickupPhotoUrls?.length > 0 || loadingPickupPhotos) && (
                    <div className="mb-4">
                      <h3 className="text-sm font-semibold text-gray-700 mb-2">Photos de collecte</h3>
                      {loadingPickupPhotos ? (
                        <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                          Chargement des photos...
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {pickupPhotoUrls?.map((url, idx) => (
                            <a key={idx} href={url} target="_blank" rel="noopener noreferrer">
                              <img
                                src={url}
                                alt={`Photo collecte ${idx + 1}`}
                                className="w-full h-32 object-cover rounded-lg border border-gray-200 hover:border-green-400 transition-colors cursor-pointer"
                              />
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Pickup Signature */}
                  {pickupTask?.client_signature_url && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-2">Signature d'enlèvement</h3>
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 inline-block">
                        <img
                          src={pickupTask?.client_signature_url}
                          alt="Signature d'enlèvement"
                          className="max-w-full h-auto max-h-32 object-contain"
                        />
                      </div>
                    </div>
                  )}

                  {/* Empty state when no photos or signature yet */}
                  {pickupPhotoUrls?.length === 0 && !loadingPickupPhotos && !pickupTask?.client_signature_url && (
                    <p className="text-sm text-gray-500">Les preuves de collecte seront disponibles prochainement.</p>
                  )}
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              {/* Photo du colis — always shown */}
              <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Photo du colis</h3>
                {(() => {
                  const photos = getPhotoUrls(shipment);
                  if (photos?.length === 0) {
                    return (
                      <div className="flex flex-col items-center justify-center py-8 text-gray-400 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                        <svg className="w-12 h-12 mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <p className="text-sm text-gray-400">Aucune photo disponible</p>
                      </div>
                    );
                  }
                  return (
                    <div className="space-y-3">
                      {photos?.map((url, idx) => (
                        <a key={idx} href={url} target="_blank" rel="noopener noreferrer">
                          <img
                            src={url}
                            alt={`Photo du colis ${idx + 1}`}
                            className="w-full rounded-lg object-cover cursor-pointer hover:opacity-90 transition-opacity"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        </a>
                      ))}
                    </div>
                  );
                })()}
              </div>

              {/* Points Earned */}
              <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-lg shadow-md p-6 text-white mb-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-bold">Points gagnés</h3>
                  <svg className="w-8 h-8 text-green-200" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                </div>
                <div className="text-3xl font-bold mb-1">
                  +{shipment?.loyalty_points_earned || 0}
                </div>
                <div className="text-sm text-green-100">
                  points de fidélité
                </div>
              </div>

              {/* Support */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Besoin d'aide ?</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Notre équipe est disponible pour répondre à vos questions
                </p>
                <a
                  href="https://wa.me/33612345678"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-full px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  Contacter le support
                </a>
              </div>

              {/* Back to Dashboard Button */}
              <div className="mt-6">
                <button
                  onClick={() => navigate('/client-dashboard')}
                  className="inline-flex items-center justify-center gap-2 w-full px-5 py-3 bg-green-600 border border-green-700 rounded-lg shadow text-sm font-semibold text-white hover:bg-green-700 hover:shadow-md transition-all"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  Retour au tableau de bord
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}